import { useCallback, useRef } from 'react'
import type { MessagePart } from 'tripPlanner/components/Chat/Message'
import {
  STREAM_EVENT,
  TOOL_NAME,
  type ItineraryUpdate,
  type EnrichmentCompleteEvent,
  type HotelSearchResultsData,
  type ExperienceSearchResultsData,
  type HotelSearchErrorData,
  type ExperienceSearchErrorData,
  type ChatUiActionData,
  type FocusSurfaceContentActionData,
  type AssistantSurfaceContentActionSnapshot,
  type ChatMetadataUpdatedData,
} from 'tripPlanner/api/types'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import { reportLog } from 'services/userMonitoring'
import { parseStreamEvent, type StreamEvent } from 'tripPlanner/utils/streamEventParser'
import { trackFirstToken, trackProgressiveUiPartFailed } from 'tripPlanner/utils/streamAnalytics'
import type { StreamMachineEvent } from './useStreamMachine'
import { parseStreamRecovery } from './streamRecovery'
import {
  applyProgressiveUiPartEvent,
  createStreamedUiPartsState,
  resetStreamedUiPartsState,
} from './streamedUiParts'

// ── Types ──────────────────────────────────────────────────────────────

interface UseStreamEventProcessorArgs {
  // Machine — single dispatch target for all state transitions
  send: (event: StreamMachineEvent) => void
  // Overlay state setters
  updateStreamingMessageParts: (parts: Array<MessagePart>) => void
  updateStreamingMessageMetadata: (metadata: {
    oaiConversationId?: string
    rawContent?: string
    generationFinishedAt?: string
    isFinalized?: boolean
  }) => void
  // Domain callbacks (wired to event bus by provider)
  onItineraryUpdates?: (chatId: number, updates: Array<ItineraryUpdate>) => void
  onEnrichmentComplete?: (chatId: number, event: EnrichmentCompleteEvent) => void
  onHotelSearchResults?: (data: HotelSearchResultsData, generation: number) => void
  onExperienceSearchResults?: (data: ExperienceSearchResultsData, generation: number) => void
  onHotelSearchError?: (data: HotelSearchErrorData, generation: number) => void
  onExperienceSearchError?: (data: ExperienceSearchErrorData, generation: number) => void
  onUiAction?: (data: ChatUiActionData) => void
  /**
   * Invoked with a paired focus action + its pre-action snapshot once both stream events have
   * arrived for the same message. The pre-snapshot always streams immediately before the action
   * (BE guarantee); an action without a matching pre-snapshot is dropped as a reconnect/replay
   * orphan rather than forwarded.
   *
   * `chatId` is `handleStreamEvent`'s own `currentChatId` parameter, not a value the caller reads
   * from React state -- for a brand-new chat, the caller's own chat-id state can still be `undefined`
   * at this exact moment (the state update from chat creation hasn't committed/re-rendered yet, so
   * any closure captured before that render is stale), while this stream is already unambiguously
   * bound to the real chat id. Passing it explicitly avoids the caller re-deriving a stale one.
   */
  onFocusSurfaceContentAction?: (payload: {
    action: FocusSurfaceContentActionData
    preSnapshot: AssistantSurfaceContentActionSnapshot
    chatId: number
  }) => void
  onChatMetadataUpdated?: (data: ChatMetadataUpdatedData) => void
  // Generation check — returns true if the given generation is still current
  isLatestGeneration: (generation: number) => boolean
  // Chat ID ref for trackFirstToken (chatId may not be in scope at event time)
  chatIdRef: React.RefObject<number | undefined>
}

export interface UseStreamEventProcessorReturn {
  /** Create handlers bound to a specific generation. Call once per stream. */
  createHandlers: (generation: number, assistantMessageId: string) => StreamHandlers
  resetBuffer: () => void
}

export interface StreamHandlers {
  restoreCatchUp: (raw: string, cursor: string, currentChatId: number) => void
  processJsonlChunk: (rawChunk: string, currentChatId: number) => void
  flushTrailingBuffer: (currentChatId: number) => void
  /** Cancel any pending RAF callback. Call on stream end or unmount. */
  dispose: () => void
}

// ── Hook ───────────────────────────────────────────────────────────────

export function useStreamEventProcessor({
  send,
  updateStreamingMessageParts,
  updateStreamingMessageMetadata,
  onItineraryUpdates,
  onEnrichmentComplete,
  onHotelSearchResults,
  onExperienceSearchResults,
  onHotelSearchError,
  onExperienceSearchError,
  onUiAction,
  onFocusSurfaceContentAction,
  onChatMetadataUpdated,
  isLatestGeneration,
  chatIdRef,
}: UseStreamEventProcessorArgs): UseStreamEventProcessorReturn {
  const streamBufferRef = useRef<string>('')

  const createHandlers = useCallback((thisGeneration: number, assistantMessageId: string): StreamHandlers => {
    let pendingRafId: number | undefined = undefined
    let pendingParts: Array<MessagePart> | undefined
    let accumulatedParts: Array<MessagePart> = []
    // Pre-action snapshots buffered until their paired focus_surface_content action arrives.
    // Scoped per-stream so they never leak across generations. A FIFO queue rather than a single
    // slot: if the model calls the focus tool more than once in one turn, multiple snapshots can
    // share the same messageId, and pairing must consume them in arrival order rather than let a
    // later snapshot silently clobber an earlier one still awaiting its action.
    const pendingContentActionSnapshots: Array<AssistantSurfaceContentActionSnapshot> = []
    const streamedUiPartsState = createStreamedUiPartsState()
    let hasDispatchedFirstContent = false
    let isRecovering = false

    const dispatchFirstContent = (normalisedParts: Array<MessagePart>) => {
      if (!hasDispatchedFirstContent && normalisedParts.length > 0) {
        hasDispatchedFirstContent = true
        send({ type: 'FIRST_CHUNK' })
        if (!isRecovering && chatIdRef.current !== undefined) {
          trackFirstToken(chatIdRef.current)
        }
      }
    }

    const schedulePendingPartsFlush = () => {
      if (isRecovering || pendingRafId !== undefined) return

      pendingRafId = requestAnimationFrame(() => {
        pendingRafId = undefined
        if (!isLatestGeneration(thisGeneration)) return

        const partsToApply = pendingParts ?? []
        pendingParts = undefined
        updateStreamingMessageParts(partsToApply)
      })
    }

    const applyPartsSnapshot = (parts: Array<MessagePart>) => {
      if (!isLatestGeneration(thisGeneration)) return

      resetStreamedUiPartsState(streamedUiPartsState)
      accumulatedParts = parts.map(p => ({ ...p }))
      pendingParts = parts
      dispatchFirstContent(parts)
      schedulePendingPartsFlush()
    }

    const applyTokenDelta = (text: string) => {
      if (!isLatestGeneration(thisGeneration)) return

      // Mutate in place — safe because RAF boundary creates new array ref for React
      const lastPart = accumulatedParts[accumulatedParts.length - 1]
      if (lastPart?.type === 'text') {
        lastPart.content += text
      } else {
        accumulatedParts.push({ type: 'text', content: text })
      }

      dispatchFirstContent(accumulatedParts)
      pendingParts = [...accumulatedParts]
      schedulePendingPartsFlush()
    }

    const scheduleAccumulatedPartsApply = () => {
      dispatchFirstContent(accumulatedParts)
      pendingParts = [...accumulatedParts]
      schedulePendingPartsFlush()
    }

    const applyUiPartEvent = (
      event:
        | Extract<StreamEvent, { event: typeof STREAM_EVENT.UI_PART_STARTED }>
        | Extract<StreamEvent, { event: typeof STREAM_EVENT.UI_PART_PATCH }>
        | Extract<StreamEvent, { event: typeof STREAM_EVENT.UI_PART_COMPLETED }>
        | Extract<StreamEvent, { event: typeof STREAM_EVENT.UI_PART_FAILED }>,
    ) => {
      if (!isLatestGeneration(thisGeneration)) return

      const result = applyProgressiveUiPartEvent({
        event,
        expectedMessageId: assistantMessageId,
        parts: accumulatedParts,
        state: streamedUiPartsState,
        onPartFailed: (uiType, reason) => {
          if (!isRecovering) trackProgressiveUiPartFailed(chatIdRef.current, uiType, reason)
        },
      })
      if (result.changed) {
        accumulatedParts = result.parts
        scheduleAccumulatedPartsApply()
      }
    }

    /** Replace buffered and rendered parts together before completion or recovery. */
    const applyFinalParts = (parts: Array<MessagePart>) => {
      if (!isLatestGeneration(thisGeneration)) return

      if (pendingRafId !== undefined) {
        cancelAnimationFrame(pendingRafId)
        pendingRafId = undefined
      }
      pendingParts = undefined
      accumulatedParts = parts.map(p => ({ ...p }))

      resetStreamedUiPartsState(streamedUiPartsState)
      dispatchFirstContent(parts)
      if (isRecovering) {
        pendingParts = accumulatedParts
      } else {
        updateStreamingMessageParts(parts)
      }
    }

    const flushPendingParts = () => {
      if (pendingRafId !== undefined) {
        cancelAnimationFrame(pendingRafId)
        pendingRafId = undefined
      }
      if (!isRecovering && pendingParts) {
        updateStreamingMessageParts(pendingParts)
        pendingParts = undefined
      }
    }

    // Shared handler for a single parsed stream event. Uses an exhaustive
    // switch so TypeScript errors at compile time if a new StreamEvent
    // variant is added but not handled here — preventing the class of bug
    // where one code-path silently drops an event type.
    const handleStreamEvent = (event: StreamEvent, currentChatId: number) => {
      switch (event.event) {
        case STREAM_EVENT.ITINERARY_UPDATE_STARTED:
          if (!isLatestGeneration(thisGeneration)) break
          send({ type: 'ITINERARY_UPDATE_START' })
          break
        case STREAM_EVENT.ITINERARY_UPDATES:
          if (!isRecovering) onItineraryUpdates?.(currentChatId, event.updates)
          if (!isLatestGeneration(thisGeneration)) break
          send({ type: 'ITINERARY_UPDATE_COMPLETE' })
          break
        case STREAM_EVENT.ENRICHMENT_COMPLETE:
          onEnrichmentComplete?.(currentChatId, event.data)
          break
        case STREAM_EVENT.CHAT_LOADING_STATE:
          if (!isLatestGeneration(thisGeneration)) break
          if (event.data.phase === 'start') {
            send({ type: 'TOOL_LOADING_START', callId: event.data.callId, key: event.data.key })
          } else {
            send({ type: 'TOOL_LOADING_COMPLETE', callId: event.data.callId })
          }
          break
        case STREAM_EVENT.CHAT_METADATA_UPDATED:
          if (!isLatestGeneration(thisGeneration)) break
          onChatMetadataUpdated?.(event.data)
          break
        case STREAM_EVENT.HOTEL_SEARCH_RESULTS:
          onHotelSearchResults?.(event.data, thisGeneration)
          break
        case STREAM_EVENT.EXPERIENCE_SEARCH_RESULTS:
          onExperienceSearchResults?.(event.data, thisGeneration)
          break
        case STREAM_EVENT.HOTEL_SEARCH_ERROR:
          onHotelSearchError?.(event.data, thisGeneration)
          break
        case STREAM_EVENT.EXPERIENCE_SEARCH_ERROR:
          onExperienceSearchError?.(event.data, thisGeneration)
          break
        case STREAM_EVENT.ASSISTANT_SURFACE_CONTENT_ACTION_SNAPSHOT:
          if (!isLatestGeneration(thisGeneration)) break
          pendingContentActionSnapshots.push(event)
          break
        case STREAM_EVENT.CHAT_UI_ACTION: {
          if (!isLatestGeneration(thisGeneration)) break
          const { data } = event
          if (data.action === 'focus_surface_content') {
            const preSnapshotIndex = pendingContentActionSnapshots.findIndex(
              (snapshot) => snapshot.messageId === data.messageId,
            )
            if (preSnapshotIndex !== -1) {
              const [preSnapshot] = pendingContentActionSnapshots.splice(preSnapshotIndex, 1)
              onFocusSurfaceContentAction?.({ action: data, preSnapshot, chatId: currentChatId })
            } else {
              reportLog(
                '[useStreamEventProcessor] Dropped focus_surface_content without matching pre-action snapshot',
                { messageId: data.messageId },
                'warn',
              )
            }
            break
          }
          onUiAction?.(data)
          break
        }
        case STREAM_EVENT.CONTENT_COMPLETE:
          if (!isLatestGeneration(thisGeneration)) break
          flushPendingParts()
          send({ type: 'CONTENT_COMPLETE' })
          break
        case STREAM_EVENT.UI_PART_STARTED:
        case STREAM_EVENT.UI_PART_PATCH:
        case STREAM_EVENT.UI_PART_COMPLETED:
        case STREAM_EVENT.UI_PART_FAILED:
          if (!isLatestGeneration(thisGeneration)) break
          applyUiPartEvent(event)
          break
        case STREAM_EVENT.MESSAGE_START:
          // With client-generated UUIDs, MESSAGE_START is just confirmation
          break
        case STREAM_EVENT.PARTS_UPDATE: {
          if (!isLatestGeneration(thisGeneration)) break
          applyPartsSnapshot(event.parts)
          break
        }
        case STREAM_EVENT.MESSAGE_END: {
          if (!isLatestGeneration(thisGeneration)) break
          // Apply final parts synchronously so the overlay has the complete
          // data (including UI components like cards/pills) before stream
          // cleanup runs. RAF batching can drop these if a parts_update
          // already scheduled a pending frame.
          applyFinalParts(event.parts)
          send({ type: 'MESSAGE_END' })

          updateStreamingMessageMetadata({
            ...(event.oaiConversationId ? { oaiConversationId: event.oaiConversationId } : {}),
            ...(event.rawContent ? { rawContent: event.rawContent } : {}),
            generationFinishedAt: new Date().toISOString(),
            isFinalized: true,
          })
          break
        }
        case STREAM_EVENT.STATUS: {
          if (!isLatestGeneration(thisGeneration)) break

          const { data } = event
          switch (data.phase) {
            case 'reasoning':
              send({ type: 'STATUS', phase: 'reasoning', summaries: data.summaries })
              break
            case 'tool_calling':
              // Skip non-user-facing tools (view switches happen instantly, no loading label needed)
              if (
                data.tool !== TOOL_NAME.SET_TRIP_PLANNER_VIEW &&
                data.tool !== TOOL_NAME.SET_TRIP_PLANNER_MOBILE_VIEW
              ) {
                send({ type: 'STATUS', phase: 'tool_calling', tool: data.tool, args: data.args })
              }
              break
            default:
              assertUnreachable(data)
          }
          break
        }
        case STREAM_EVENT.HEARTBEAT:
          if (!isLatestGeneration(thisGeneration)) break
          send({ type: 'HEARTBEAT' })
          break
        case STREAM_EVENT.TOKEN_BATCH: {
          if (!isLatestGeneration(thisGeneration)) break
          applyTokenDelta(event.text)
          break
        }
        default:
          assertUnreachable(event)
      }
    }

    const processJsonlChunk = (rawChunk: string, currentChatId: number) => {
      streamBufferRef.current += rawChunk
      const lines = streamBufferRef.current.split('\n')
      streamBufferRef.current = lines.pop() ?? ''

      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed) continue

        const event = parseStreamEvent(trimmed)
        if (event) {
          handleStreamEvent(event, currentChatId)
        } else {
          console.warn('[trip-chat] Failed to parse stream event:', trimmed.substring(0, 200))
        }
      }
    }

    const flushTrailingBuffer = (currentChatId: number) => {
      const trailing = streamBufferRef.current.trim()
      if (trailing) {
        const event = parseStreamEvent(trailing)
        if (event) {
          handleStreamEvent(event, currentChatId)
        } else {
          console.warn('[trip-chat] Failed to parse trailing buffer:', trailing.substring(0, 200))
        }
      }
      streamBufferRef.current = ''
    }

    const dispose = () => {
      if (pendingRafId !== undefined) {
        cancelAnimationFrame(pendingRafId)
        pendingRafId = undefined
      }
      pendingParts = undefined
      accumulatedParts = []
      resetStreamedUiPartsState(streamedUiPartsState)
    }

    const restoreCatchUp = (raw: string, cursor: string, currentChatId: number) => {
      if (!isLatestGeneration(thisGeneration)) return
      const checkpoint = parseStreamRecovery(raw, assistantMessageId, cursor)
      streamBufferRef.current = ''
      pendingContentActionSnapshots.length = 0
      if (checkpoint.kind === 'legacy') {
        applyFinalParts(checkpoint.text ? [{ type: 'text', content: checkpoint.text }] : [])
        return
      }

      dispose()
      hasDispatchedFirstContent = false
      send({ type: 'RECOVERY_START' })
      isRecovering = true
      try {
        for (const event of checkpoint.events) handleStreamEvent(event, currentChatId)
      } finally {
        isRecovering = false
      }
      // Keep progressive-part indexes: the next live patch can complete a recovered card.
      pendingParts = [...accumulatedParts]
      flushPendingParts()
    }

    return { processJsonlChunk, flushTrailingBuffer, restoreCatchUp, dispose }
  }, [
    send,
    isLatestGeneration,
    onItineraryUpdates,
    onEnrichmentComplete,
    onHotelSearchResults,
    onExperienceSearchResults,
    onHotelSearchError,
    onExperienceSearchError,
    onUiAction,
    onFocusSurfaceContentAction,
    onChatMetadataUpdated,
    updateStreamingMessageParts,
    updateStreamingMessageMetadata,
    chatIdRef,
  ])

  const resetBuffer = useCallback(() => {
    streamBufferRef.current = ''
  }, [])

  return { createHandlers, resetBuffer }
}
